import * as sessionReplay from '@amplitude/session-replay-browser';
import { type AnalyticsBrowser } from '@segment/analytics-next';
import Cookie from 'js-cookie';

const getStoredSessionId = () => {
  const sessionIdCookie = Cookie.get('analytics_session_id');
  return sessionIdCookie ? Number.parseInt(sessionIdCookie) : 0;
};

/**
 *  initialize session replay according to the documentation
 *  https://amplitude.com/docs/session-replay/session-replay-integration-with-segment#amplitude-actions-destination
 */
export const initAmplitudeSessionReplay = async ({
  amplitudeApiKey,
  sessionReplayOptions,
  segmentInstance,
}: {
  amplitudeApiKey: string;
  segmentInstance: AnalyticsBrowser;
  sessionReplayOptions: sessionReplay.SessionReplayOptions;
}) => {
  const storedSessionId = getStoredSessionId();
  const user = await segmentInstance.user();

  const params = {
    ...sessionReplayOptions,
    debugMode: true,
    sessionId: storedSessionId,
    deviceId: user.anonymousId() || undefined,
  };

  await sessionReplay.init(amplitudeApiKey, params).promise;

  // Add middleware to check if the session id has changed,
  // and update the session replay instance
  segmentInstance.addSourceMiddleware(({ payload, next }) => {
    const sessionId = getStoredSessionId();
    const amplitudeIntegration =
      payload.obj.integrations?.['Amplitude (Actions)'];
    const nextSessionId =
      typeof amplitudeIntegration === 'object' && !!amplitudeIntegration
        ? (amplitudeIntegration as { session_id?: number }).session_id || 0
        : 0;

    if (sessionId < nextSessionId) {
      Cookie.set('analytics_session_id', String(nextSessionId));
      sessionReplay.setSessionId(nextSessionId);
    }
    next(payload);
  });

  // Add middleware to always add session replay properties to track calls
  segmentInstance.addSourceMiddleware(({ payload, next }) => {
    const sessionReplayProperties = sessionReplay.getSessionReplayProperties();
    if (payload.type() === 'track') {
      payload.obj.properties = {
        ...payload.obj.properties,
        ...sessionReplayProperties,
      };
    }

    next(payload);
  });
};
